import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ReloadOutlined,
  SearchOutlined,
  MailOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import "./PartnerAdminDashboard.css";

// ปรับ URL นี้ให้ตรงกับ backend จริงของคุณ (เหมือนกับใน PartnerApplyForm.jsx)
const API_URL = process.env.REACT_APP_API || "http://localhost:4000/api";

const STATUS_LABEL = {
  pending: "รอตรวจสอบ",
  reviewing: "กำลังตรวจสอบ",
  approved: "อนุมัติแล้ว",
  rejected: "ปฏิเสธ",
};

const STATUS_OPTIONS = ["all", "pending", "reviewing", "approved", "rejected"];

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("th-TH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PartnerAdminDashboard = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedPartner, setSelectedPartner] = useState(null); // partner ที่กำลังดูรายละเอียด

  const fetchPartners = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/partner`);
      if (!res.ok) throw new Error("โหลดข้อมูลไม่สำเร็จ");
      const data = await res.json();
      setPartners(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  // ปิด modal ด้วย Esc + ล็อกการ scroll พื้นหลังตอน modal เปิดอยู่
  useEffect(() => {
    if (!selectedPartner) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") setSelectedPartner(null);
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [selectedPartner]);

  const filteredPartners = useMemo(() => {
    return partners.filter((p) => {
      const matchStatus =
        statusFilter === "all" || p.status === statusFilter;

      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        p.name?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q) ||
        p.phone?.toLowerCase().includes(q) ||
        p.organization?.toLowerCase().includes(q);

      return matchStatus && matchSearch;
    });
  }, [partners, search, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: partners.length };
    for (const p of partners) {
      counts[p.status] = (counts[p.status] || 0) + 1;
    }
    return counts;
  }, [partners]);

  return (
    <div className="partner-admin-page">
      <div className="partner-admin-header">
        <div>
          <h2 className="partner-admin-title">รายการสมัคร Partner</h2>
          <p className="partner-admin-subtitle">
            ทั้งหมด {partners.length} รายการ
          </p>
        </div>
        <div className="partner-admin-header-actions">
          {/* ทางลัดไปหน้าเพิ่มอีเมล / ส่งคำเชิญ partner */}
          <Link to="/admin/partner-leads" className="partner-admin-lead-link">
            <MailOutlined /> เพิ่มอีเมลเชิญ Partner
          </Link>
          <button
            className="partner-admin-refresh-btn"
            onClick={fetchPartners}
            disabled={loading}
          >
            <ReloadOutlined spin={loading} /> รีเฟรช
          </button>
        </div>
      </div>

      {/* Filter แถบสถานะ */}
      <div className="partner-admin-filters">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            className={`partner-admin-filter-chip ${
              statusFilter === s ? "active" : ""
            }`}
            onClick={() => setStatusFilter(s)}
          >
            {s === "all" ? "ทั้งหมด" : STATUS_LABEL[s]}
            <span className="chip-count">{statusCounts[s] || 0}</span>
          </button>
        ))}
      </div>

      {/* ช่องค้นหา */}
      <div className="partner-admin-search">
        <SearchOutlined />
        <input
          type="text"
          placeholder="ค้นหาชื่อ, อีเมล, เบอร์โทร, องค์กร..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* เนื้อหา */}
      {loading && <div className="partner-admin-state">กำลังโหลดข้อมูล...</div>}

      {!loading && error && (
        <div className="partner-admin-state error">
          ❌ {error}
          <button onClick={fetchPartners}>ลองใหม่</button>
        </div>
      )}

      {!loading && !error && filteredPartners.length === 0 && (
        <div className="partner-admin-state">ไม่พบข้อมูล</div>
      )}

      {!loading && !error && filteredPartners.length > 0 && (
        <div className="partner-admin-table-wrap">
          <table className="partner-admin-table">
            <thead>
              <tr>
                <th>ชื่อผู้ติดต่อ</th>
                <th>เบอร์โทร</th>
                <th>อีเมล</th>
                <th>องค์กร</th>
                <th>สินค้าที่แนบมา</th>
                <th>สถานะ</th>
                <th>วันที่สมัคร</th>
              </tr>
            </thead>
            <tbody>
              {filteredPartners.map((p) => (
                <tr
                  key={p._id}
                  className="partner-row-clickable"
                  onClick={() => setSelectedPartner(p)}
                >
                  <td>{p.name}</td>
                  <td>{p.phone}</td>
                  <td>{p.email}</td>
                  <td>{p.organization}</td>
                  <td>
                    <span className="product-count-badge">
                      <ShoppingOutlined /> {p.products?.length || 0} รายการ
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-${p.status}`}>
                      {STATUS_LABEL[p.status] || p.status}
                    </span>
                  </td>
                  <td>{formatDate(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== Modal รายละเอียด partner: หมวดหมู่ + สินค้าที่แนบมา (custom, ไม่ใช้ antd) ===== */}
      {selectedPartner && (
        <div
          className="partner-detail-overlay"
          onClick={() => setSelectedPartner(null)}
        >
          <div
            className="partner-detail-box"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="partner-detail-close"
              onClick={() => setSelectedPartner(null)}
              aria-label="ปิด"
            >
              ✕
            </button>

            <h3 className="partner-detail-title">
              รายละเอียด: {selectedPartner.name}
            </h3>

            <div className="partner-detail-modal">
              {/* ข้อมูลติดต่อ */}
              <div className="partner-detail-contact">
                <div>
                  <span className="detail-label">เบอร์โทร:</span>{" "}
                  {selectedPartner.phone}
                </div>
                <div>
                  <span className="detail-label">อีเมล:</span>{" "}
                  {selectedPartner.email}
                </div>
                <div>
                  <span className="detail-label">องค์กร:</span>{" "}
                  {selectedPartner.organization}
                </div>
                <div>
                  <span className="detail-label">สถานะ:</span>{" "}
                  <span
                    className={`status-badge status-${selectedPartner.status}`}
                  >
                    {STATUS_LABEL[selectedPartner.status] ||
                      selectedPartner.status}
                  </span>
                </div>
                <div>
                  <span className="detail-label">วันที่สมัคร:</span>{" "}
                  {formatDate(selectedPartner.createdAt)}
                </div>
              </div>

              {/* หมวดหมู่ที่สนใจ */}
              <div className="partner-detail-section">
                <h4>หมวดหมู่สินค้าที่สนใจ</h4>
                {selectedPartner.categories?.length > 0 ? (
                  <div className="partner-detail-categories">
                    {selectedPartner.categories.map((cat) => (
                      <span className="category-tag" key={cat._id}>
                        {cat.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="detail-empty-text">ไม่ได้เลือกหมวดหมู่</p>
                )}
              </div>

              {/* รายการสินค้าที่แนบมา */}
              <div className="partner-detail-section">
                <h4>
                  สินค้าที่แนบมา ({selectedPartner.products?.length || 0}{" "}
                  รายการ)
                </h4>
                {selectedPartner.products?.length > 0 ? (
                  <div className="partner-detail-products">
                    {selectedPartner.products.map((prod) => (
                      <div
                        className="partner-detail-product-card"
                        key={prod._id}
                      >
                        <div className="product-card-images">
                          {prod.images?.length > 0 ? (
                            prod.images.map((img, i) => (
                              <img
                                key={img.fileName || img.public_id || i}
                                src={img.url}
                                alt={prod.name}
                                className="product-card-img"
                              />
                            ))
                          ) : (
                            <div className="product-card-no-image">
                              ไม่มีรูป
                            </div>
                          )}
                        </div>
                        <div className="product-card-info">
                          <h5>{prod.name}</h5>
                          {prod.description && (
                            <p className="product-card-desc">
                              {prod.description}
                            </p>
                          )}
                          {prod.price !== undefined && prod.price !== null && (
                            <p className="product-card-price">
                              ราคาโดยประมาณ: {prod.price.toLocaleString()} บาท
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="detail-empty-text">
                    partner รายนี้ยังไม่ได้แนบสินค้ามา
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerAdminDashboard;